import React, { useEffect } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { KButton } from "@kyburz/kdesign-system";
import { useDispatch } from "react-redux";
import useSetState from "../../hooks/useTypedSetState";
import { setAlert } from "../../redux/actions/actions";
import BaseLayout from "../../components/layouts/BaseLayout";
import {
  useCreateSMSCommand,
  useGetSMSCommand,
  useUpdateSMSCommand,
} from "../../apiCalls/config/sms";
import SMSCommandEditor from "./SMSCommandEditor";
import { useStyles } from "./styles";

interface RouteParams {
  templateId: string;
}

interface Props {
  edit?: boolean;
}

const CreateSMSCommand: React.FC<Props> = ({ edit = false }) => {
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();

  const match = useRouteMatch({
    path: "/config/sms/:templateId",
    strict: true,
    sensitive: true,
  });
  const { templateId } = match?.params as RouteParams;

  const { loading: saveLoading, request: createSMSCommand } =
    useCreateSMSCommand();
  const { request: getSMSCommand } = useGetSMSCommand();
  const { request: updateSMSCommand } = useUpdateSMSCommand();

  const title = edit ? "Edit SMS Command" : "Create New SMS Command";
  const goBack = () => history.push("/config/sms");

  const [SMSCommand, setSMSCommand] = useSetState({
    id: "",
    name: "",
    command: "",
    ignition_on: false,
    bulk_execution_enabled: false,
    companies: ["kyburz"],
  });

  useEffect(() => {
    if (edit) {
      getSMSCommand(templateId).then((res: any) => {
        setSMSCommand({
          id: res?._id,
          name: res?.name,
          command: res?.command,
          ignition_on: res?.ignition_on,
          bulk_execution_enabled: res?.bulk_execution_enabled,
          companies: res?.companies,
        });
      });
    }
  }, []);

  const createNewSMSCommand = () => {
    createSMSCommand(SMSCommand)
      .then(() => {
        dispatch(
          setAlert(
            `SMS Command '${SMSCommand.name}' has been saved`,
            "success",
          ),
        );
        goBack();
      })
      .catch((err) => {
        dispatch(setAlert(err.message || "An error occurred", "error"));
        console.error(err);
      });
  };

  const updateSMS = () => {
    updateSMSCommand(SMSCommand)
      .then(() => {
        dispatch(
          setAlert(
            `SMS Command '${SMSCommand.name}' has been saved`,
            "success",
          ),
        );
        goBack();
      })
      .catch((err) => {
        dispatch(setAlert(err.message || "An error occurred", "error"));
        console.error(err);
      });
  };

  const onSave = () => {
    if (edit) {
      updateSMS();
      return;
    }
    createNewSMSCommand();
  };

  return (
    <BaseLayout
      title={title}
      handleArrowClick={goBack}
      mainClassname={classes.layout}
    >
      <SMSCommandEditor
        SMSCommand={{ ...SMSCommand, id: undefined }}
        onSMSCommandChanged={setSMSCommand}
      />
      <footer className={classes.buttons}>
        <KButton children="Cancel" theme="secondary" onClick={goBack} />
        <KButton
          children="Save and close"
          onClick={onSave}
          loading={saveLoading}
        />
      </footer>
    </BaseLayout>
  );
};

export default CreateSMSCommand;
